import { useCallback, useEffect } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import useConfig from '@sdk/config/useConfig';

import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import { useNavigation } from '@web/navigation/NavigationProvider';
import {
  MODAL_APPEARANCE_DELAY,
  MODAL_DISMISS_LIMIT,
} from '@web/account/billingWarnings/MissingBillingAddressModal/missingBillingAddress.consts';
import { getIsAddressUpdateRequired } from '@web/account/utils/getIsAddressUpdateRequired';
import { BILLING_ADDRESS_WARNING_KEY } from '@web/account/constants';

import { Props } from './MissingBillingAddressModal';
import { shouldShowBillingModal } from './missingBillingAddress.utils';

export const useMissingBillingAddressModal = ({ currentUser, saveClientFlags }: Props) => {
  const { navigate } = useNavigation();
  const { isVisible, open, close } = useModalState();
  const { shouldShowModal, modalOpenedCount } = useWarningModalVisibility(currentUser);
  const isDismissable = modalOpenedCount <= MODAL_DISMISS_LIMIT;

  const onModalOpen = useCallback(async () => {
    open();
    await saveClientFlags({
      flags: {
        [BILLING_ADDRESS_WARNING_KEY]: {
          date: new Date().toISOString(),
          count: modalOpenedCount + 1,
        },
      },
    });
  }, [modalOpenedCount, open, saveClientFlags]);

  useEffect(() => {
    if (shouldShowModal && !isVisible) {
      const timeoutId = setTimeout(() => {
        onModalOpen();
      }, MODAL_APPEARANCE_DELAY);

      return () => clearTimeout(timeoutId);
    }
  }, [isVisible, onModalOpen, open, shouldShowModal]);

  const onClose = () => {
    close();
  };

  const navigateToBilling = () => {
    close();
    navigate('account.overview.billing', { fromBillingAddressModal: true });
  };

  return {
    isDismissable,
    isVisible,
    onClose,
    navigateToBilling,
  };
};

function useWarningModalVisibility(currentUser: AuthUserType) {
  const { billingInfo } = useBillingInfo();
  const { FEATURE_FLAGS } = useConfig();
  const billingAddressWarningClientFlags =
    currentUser.settings.clientFlags[BILLING_ADDRESS_WARNING_KEY];
  const { isToastBilling, isPremium } = currentUser.org;

  const modalOpenedCount = billingAddressWarningClientFlags?.count || 0;
  const lastShownDate = billingAddressWarningClientFlags?.date
    ? new Date(billingAddressWarningClientFlags.date)
    : undefined;

  const shouldShowModal =
    FEATURE_FLAGS.TAX_CALCULATIONS_ENABLED &&
    getIsAddressUpdateRequired({
      isManager: currentUser.isManager,
      isToastBilling,
      isPremium,
      billingInfo,
    }) &&
    shouldShowBillingModal(currentUser.isManager, modalOpenedCount, lastShownDate);

  return {
    shouldShowModal,
    modalOpenedCount,
  };
}
